import { useReducedMotion } from 'hooks/useReducedMotion';
import { m } from 'motion/react';
import { type ComponentProps, createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { cn } from 'utils/cn';

/*
 * How the kit's tabs and drawers move, and where the Crespect AI surfaces put the kit's overlays.
 *
 * The tab list's indicator and the kit `Drawer` move by transitions.dev's recipes, on the motion tokens in
 * `src/index.css`. The kit's popups, dialogs and tooltips keep their own enter and exit, everywhere.
 *
 * Each motion is written twice, in full, so Tailwind finds both: `new:`-prefixed for the rest of the app, where
 * classic keeps the generated look, and unprefixed for the AI surfaces, which move this way in both designs. A kit
 * part takes the set its place calls for from `useOverlayScope()`.
 */

/** The tab list's sliding indicator, hidden where it does not apply: classic keeps the tabs it had. */
const TAB_INDICATOR_MOTION =
    'hidden new:block new:transition-[translate,width] new:duration-(--duration-fast) new:ease-(--ease-smooth-out) new:motion-reduce:transition-none';

/** The indicator's tween for a Motion element that slides as it does: `--duration-fast` on `--ease-smooth-out`. */
export const TAB_INDICATOR_TRANSITION = { type: 'tween', duration: 0.25, ease: [0.22, 1, 0.36, 1] } as const;

/** The pill's corners, `rounded-sm`: set in `style` as px, which Motion's scale correction reads. */
const PILL_RADIUS = 6;

/**
 * A segmented control's pill, rendered in the chosen segment alone: it slides to the next one on the indicator's
 * tween, inside the control's own `LayoutGroup`. `className` gives its fill.
 */
export function SegmentPill({ className }: { className: string }) {
    const reduced = useReducedMotion();
    return (
        <m.span
            layoutId="pill"
            className={cn('shadow-segment absolute inset-0 -z-10', className)}
            style={{ borderRadius: PILL_RADIUS }}
            transition={reduced ? { duration: 0 } : TAB_INDICATOR_TRANSITION}
        />
    );
}

/**
 * A tab's label. A hover, seen all day, changes at once, as does the label the selection leaves; the chosen label's
 * colour follows the indicator on its clock, in 150ms under reduced motion, where the indicator jumps. Its colour
 * alone: a hover's wash on the chosen tab changes at once too.
 */
const TAB_MOTION =
    'new:transition-[color,background-color] new:duration-0 new:data-active:transition-[color] new:data-active:duration-(--duration-fast) new:ease-(--ease-smooth-out) new:motion-reduce:data-active:duration-150';

/**
 * The kit `Drawer` slides along its own axis with a cross-blur (panel reveal); a close follows the swipe's
 * strength, as the generated drawer's does. Its backdrop fades on the same clocks. Under reduced motion the
 * generated drawer's 150ms fade stays, which the new design's clocks would otherwise outrank (`!` on the backdrop's,
 * over the release rule). A swipe follows the finger with no duration and a release closes on the swipe's strength,
 * as the kit's rules say; the `new:` clocks outrank those too, so they are restated here.
 */
const DRAWER_MOTION =
    'new:duration-(--duration-slow) new:data-ending-style:duration-[calc(var(--drawer-swipe-strength)*var(--duration-medium))] new:data-swiping:duration-0 new:data-nested-drawer-swiping:duration-0 new:data-ending-style:data-swiping:duration-[calc(var(--drawer-swipe-strength)*var(--duration-medium))] new:data-ending-style:data-nested-drawer-swiping:duration-[calc(var(--drawer-swipe-strength)*var(--duration-medium))] new:motion-safe:data-starting-style:blur-(--blur-small) new:motion-safe:data-ending-style:blur-(--blur-small) new:motion-reduce:duration-150';

/**
 * Over `DRAWER_MOTION`, a kit `Drawer` drawn as a side panel (`DETACHED_DRAWER_CLASS`) moves as the new design's other
 * side panels do: it travels 70% of its width, in out of a fade and a blur, and leaves quicker, fading into the blur
 * on its way back. A swipe still drags it, and a release finishes from where it is let go, on the swipe's strength.
 */
export const SIDE_PANEL_MOTION =
    'new:ease-(--ease-smooth-out) new:data-ending-style:duration-[calc(var(--drawer-swipe-strength)*var(--duration-fast))] new:data-ending-style:data-swiping:duration-[calc(var(--drawer-swipe-strength)*var(--duration-fast))] new:data-ending-style:data-nested-drawer-swiping:duration-[calc(var(--drawer-swipe-strength)*var(--duration-fast))] new:data-starting-style:opacity-0 new:data-ending-style:opacity-0 new:data-[swipe-direction=right]:[--closed-transform:translate3d(70%,0,0)] new:motion-safe:data-starting-style:blur-(--blur-panel) new:motion-safe:data-ending-style:blur-(--blur-panel)';

const DRAWER_BACKDROP_MOTION =
    'new:duration-(--duration-slow) new:ease-(--ease-smooth-out) new:data-ending-style:duration-[calc(var(--drawer-swipe-strength)*var(--duration-medium))] new:data-swiping:duration-0 new:data-ending-style:data-swiping:duration-[calc(var(--drawer-swipe-strength)*var(--duration-medium))] new:motion-reduce:duration-150 new:motion-reduce:data-ending-style:duration-150!';

const SITE_MOTION = {
    tabIndicator: TAB_INDICATOR_MOTION,
    tab: TAB_MOTION,
    drawer: DRAWER_MOTION,
    drawerBackdrop: DRAWER_BACKDROP_MOTION,
};

const AI_MOTION: typeof SITE_MOTION = {
    tabIndicator:
        'block transition-[translate,width] duration-(--duration-fast) ease-(--ease-smooth-out) motion-reduce:transition-none',
    tab: 'transition-[color,background-color] duration-0 data-active:transition-[color] data-active:duration-(--duration-fast) ease-(--ease-smooth-out) motion-reduce:data-active:duration-150',
    drawer: 'duration-(--duration-slow) data-ending-style:duration-[calc(var(--drawer-swipe-strength)*var(--duration-medium))] motion-safe:data-starting-style:blur-(--blur-small) motion-safe:data-ending-style:blur-(--blur-small)',
    // The AI surfaces' sheets dim the page as their dialogs do, with no blur: what is under them stays legible.
    drawerBackdrop:
        'new:bg-overlay supports-backdrop-filter:backdrop-blur-none duration-(--duration-slow) ease-(--ease-smooth-out) data-ending-style:duration-[calc(var(--drawer-swipe-strength)*var(--duration-medium))]',
};

export type OverlayScope = {
    /** The motion classes for this place: unprefixed on an AI surface, `new:` elsewhere. */
    motion: typeof SITE_MOTION;
    /** Where a kit popup portals: the AI surface's container, or `<body>` when undefined. */
    container: HTMLElement | undefined;
    /** Whether this is an AI surface, where the new motion applies in both designs. */
    ai: boolean;
};

const SITE: OverlayScope = { motion: SITE_MOTION, container: undefined, ai: false };

const OverlayScopeContext = createContext<OverlayScope>(SITE);

/** The motion and portal container of the place a kit part renders in. Context crosses portals, so a popup's popup gets its opener's. */
export function useOverlayScope(): OverlayScope {
    return useContext(OverlayScopeContext);
}

/** Fixed at the AI dialog tier and isolated, so whatever a surface opens stacks above the floating panel together. */
function createPortalContainer() {
    const container = document.createElement('div');
    container.dataset.design = 'new';
    container.className = 'fixed top-0 left-0 isolate z-(--z-floating-ai-dialog)';
    return container;
}

/**
 * The root of a Crespect AI surface: the floating panel, the AI page's content beside the app chrome, the phone
 * screen. It draws in the new design's tokens in both designs, by carrying `data-design="new"`: the new design's
 * block and its dark block, and the `new:` variant, apply under it, while classic's chrome around it keeps classic's
 * colours. The kit popups opened inside it portal into a container under `<body>` that carries the attribute too,
 * and its tabs and drawers move by the AI motion. `new:` matches descendants only, so the root's own classes take
 * no `new:` variant; and `color` inherits as body computed it, so the root states its own text colour.
 */
/**
 * A piece of the new design inside a classic page, such as the reports' quick-filter chips. It carries
 * `data-design="new"`, so the new design's tokens and the `new:` variant apply under it, and the kit popups opened
 * inside it portal into a container under `<body>` that carries the attribute too. The container takes no stacking
 * tier of its own: each popup's positioner keeps the page tier it has (`--z-dropdown`).
 */
export function NewDesignIsland(props: ComponentProps<'div'>) {
    const [container] = useState(() => {
        const element = document.createElement('div');
        element.dataset.design = 'new';
        return element;
    });
    useLayoutEffect(() => {
        document.body.append(container);
        return () => container.remove();
    }, [container]);
    const scope = useMemo(() => ({ ...SITE, container }), [container]);
    return (
        <OverlayScopeContext.Provider value={scope}>
            <div {...props} data-design="new" />
        </OverlayScopeContext.Provider>
    );
}

export function AiSurface(props: ComponentProps<'div'>) {
    const [container] = useState(createPortalContainer);
    useLayoutEffect(() => {
        document.body.append(container);
        return () => container.remove();
    }, [container]);
    const scope = useMemo(() => ({ motion: AI_MOTION, container, ai: true }), [container]);
    return (
        <OverlayScopeContext.Provider value={scope}>
            <div {...props} data-design="new" />
        </OverlayScopeContext.Provider>
    );
}
